// Shared promotion helpers used by both the customer portal and the
// business-side manual order flow. Functions are pure and null-safe so they
// can be used for guest orders (no customer profile) as well.

/**
 * Determine whether a promotion can be applied to the current cart.
 * @param {object} promo - Discount (camelCase) as produced by promotionsService.
 * @param {Array} cart - Cart items ({ prodId, name, categoryName, price, quantity }).
 * @param {object} [ctx]
 * @param {object|null} [ctx.customer] - Customer profile, or null for a guest.
 * @param {string|null} [ctx.customerId] - Customer id, or null for a guest.
 * @param {Array} [ctx.redemptions] - Prior redemptions for customer-limit checks.
 * @returns {{ eligible: boolean, reason?: string }}
 */
export const isPromotionEligible = (promo, cart, ctx = {}) => {
    const { customer = null, customerId = null, redemptions = [] } = ctx;

    if (!promo || !cart || cart.length === 0) return { eligible: false, reason: 'Sepet boş' };

    // 1. Basic Validity (Roles, Limits, Dates, Days)
    if (promo.isActive === false) return { eligible: false, reason: 'Bu kampanya artık aktif değil.' };

    const now = new Date();
    const today = now.getDay();
    const yesterday = (today + 6) % 7;
    const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

    if (promo.startTime && promo.expiryTime) {
        const [startH, startM] = promo.startTime.split(':').map(Number);
        const [endH, endM] = promo.expiryTime.split(':').map(Number);
        const startTotalMinutes = startH * 60 + startM;
        const endTotalMinutes = endH * 60 + endM;

        const isTodayApplicable = !promo.applicableDays || !promo.applicableDays.length || promo.applicableDays.includes(today);
        const isYesterdayApplicable = !promo.applicableDays || !promo.applicableDays.length || promo.applicableDays.includes(yesterday);

        let activeNow = false;

        if (isTodayApplicable) {
            if (startTotalMinutes <= endTotalMinutes) {
                if (currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes) activeNow = true;
            } else {
                if (currentTotalMinutes >= startTotalMinutes) activeNow = true;
            }
        }

        if (!activeNow && isYesterdayApplicable) {
            if (startTotalMinutes > endTotalMinutes) {
                if (currentTotalMinutes <= endTotalMinutes) activeNow = true;
            }
        }

        if (!activeNow) {
            return { eligible: false, reason: `Kampanya şu an geçerli değil. Geçerli saatler: ${promo.startTime} - ${promo.expiryTime}` };
        }
    } else if (promo.applicableDays && Array.isArray(promo.applicableDays) && promo.applicableDays.length > 0) {
        if (!promo.applicableDays.includes(today)) {
            return { eligible: false, reason: 'Bu kampanya bugün geçerli değil.' };
        }
    }

    // Overall Campaign Date Check
    if (promo.startDate) {
        const sDate = new Date(promo.startDate);
        if (new Date() < sDate) return { eligible: false, reason: 'Kampanya henüz başlamadı.' };
    }
    if (promo.expiryDate) {
        const eDate = new Date(promo.expiryDate);
        eDate.setHours(23, 59, 59, 999);
        if (new Date() > eDate) return { eligible: false, reason: 'Kampanya sona erdi.' };
    }

    // Target filtering. Without a customer profile (guest order), only
    // "all" campaigns can be validated.
    let targetMatch = false;
    if (promo.target === 'all') targetMatch = true;
    else if (customer) {
        if (promo.target === 'groups') {
            const allowedLevels = Array.isArray(promo.loyaltyStatus) ? promo.loyaltyStatus : (promo.loyaltyStatus ? [promo.loyaltyStatus] : []);
            const allowedRoles = Array.isArray(promo.visitRole) ? promo.visitRole : (promo.visitRole ? [promo.visitRole] : []);
            if (allowedLevels.includes(customer.loyaltyLevel) || allowedRoles.includes(customer.visitRole)) targetMatch = true;
        } else if (promo.target === 'visit_role') {
            const allowed = Array.isArray(promo.visitRole) ? promo.visitRole : (promo.visitRole ? [promo.visitRole] : []);
            if (allowed.includes(customer.visitRole)) targetMatch = true;
        } else if (promo.target === 'customers' && promo.selectedCustomers?.includes(customerId)) {
            targetMatch = true;
        }
    }

    if (!targetMatch) return { eligible: false, reason: 'Bu kampanya sınıfa/role özeldir.' };

    // Total Limit check
    if (promo.totalLimit && (promo.usageCount || 0) >= promo.totalLimit) {
        return { eligible: false, reason: 'Kampanya toplam kullanım sınırına ulaştı.' };
    }

    // Customer limit check
    if (promo.customerLimit) {
        const usedCount = (redemptions || []).filter(r => r.appliedPromotionId === promo.id || r.discountId === promo.id).length;
        if (usedCount >= promo.customerLimit) {
            return { eligible: false, reason: `Bu kampanyayı en fazla ${promo.customerLimit} kez kullanabilirsiniz.` };
        }
    }

    // 2. Amount and Products
    const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);

    if (promo.minAmount && subtotal < Number(promo.minAmount)) {
        return { eligible: false, reason: `Min. ₺${promo.minAmount} sepet tutarı gerekli. (Şu an: ₺${subtotal})` };
    }

    // Specific Product Scope Check
    if (promo.productScope === 'specific' || promo.productScope === 'specific_products') {
        const selectedList = promo.selectedProducts || [];
        const selectedCategories = promo.selectedCategories || [];

        if (selectedList.length > 0 || selectedCategories.length > 0) {
            const hasRequiredItem = cart.some(item =>
                selectedList.includes(item.prodId) ||
                selectedList.includes(item.name) ||
                selectedCategories.includes(item.categoryName)
            );
            if (!hasRequiredItem) {
                return { eligible: false, reason: 'Kampanyalı kategoriler/ürünlerden en az biri eklenmeli.' };
            }
        }
    }

    // 3. Bundle/Buy-Get logic
    if (promo.campaignType === 'bundle' || promo.campaignType === 'coupon_bundle') {
        const buyItems = promo.buyItems || [];
        if (buyItems.length > 0) {
            const hasBuyItem = cart.some(item =>
                buyItems.includes(item.prodId) ||
                buyItems.includes(item.name)
            );
            if (!hasBuyItem) {
                return { eligible: false, reason: 'Kampanyalı ürünler sepetinizde bulunamadı.' };
            }
        }
    }

    return { eligible: true };
};

/**
 * Compute the monetary discount a promotion applies to the cart.
 * Mirrors the portal's cart math (bundle / percentage / fixed).
 * Multiplier campaigns yield 0 here (they only affect earned points).
 * @returns {{ promoDiscount: number, promoDetails: Array<{name:string, amount:number, quantity:number}> }}
 */
export const calculatePromotionDiscount = (appliedPromotion, cart) => {
    const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
    let promoDiscount = 0;
    const promoDetails = [];

    if (!appliedPromotion) return { promoDiscount: 0, promoDetails };

    if (appliedPromotion.campaignType === 'bundle' || appliedPromotion.campaignType === 'coupon_bundle') {
        const buyItems = appliedPromotion.buyItems || [];
        const getItems = appliedPromotion.getItems?.length > 0 ? appliedPromotion.getItems : buyItems;
        const buyQty = Number(appliedPromotion.buyQuantity) || 1;
        const getQty = Number(appliedPromotion.getQuantity) || 1;
        const discountPercent = Number(appliedPromotion.bundleDiscountValue) || 100;

        const totalBuyInCart = cart
            .filter(item => buyItems.includes(item.prodId) || buyItems.includes(item.name))
            .reduce((sum, item) => sum + item.quantity, 0);

        const completedSets = Math.min(1, Math.floor(totalBuyInCart / buyQty));

        if (completedSets > 0) {
            const maxDiscountable = completedSets * getQty;
            const targetItems = cart
                .filter(item => getItems.includes(item.prodId) || getItems.includes(item.name))
                .sort((a, b) => Number(a.price) - Number(b.price));

            let remainingDiscountable = maxDiscountable;
            for (const itm of targetItems) {
                if (remainingDiscountable <= 0) break;
                const canDiscount = Math.min(itm.quantity, remainingDiscountable);
                const amount = canDiscount * Number(itm.price) * (discountPercent / 100);
                promoDiscount += amount;
                promoDetails.push({ name: itm.name, amount, quantity: canDiscount });
                remainingDiscountable -= canDiscount;
            }
        }
    } else if (appliedPromotion.type === 'multiplier') {
        promoDiscount = 0;
    } else if (appliedPromotion.type === 'percentage') {
        const isSpecific = appliedPromotion.productScope === 'specific' || appliedPromotion.productScope === 'specific_products';
        if (isSpecific) {
            const selectedList = appliedPromotion.selectedProducts || [];
            const selectedCategories = appliedPromotion.selectedCategories || [];

            const matchingItems = cart.filter(item =>
                selectedList.includes(item.prodId) ||
                selectedList.includes(item.name) ||
                selectedCategories.includes(item.categoryName)
            ).sort((a, b) => Number(a.price) - Number(b.price));

            if (matchingItems.length > 0) {
                const amount = Number(matchingItems[0].price) * (Number(appliedPromotion.value) / 100);
                promoDiscount = amount;
                promoDetails.push({ name: matchingItems[0].name, amount, quantity: 1 });
            }
        } else {
            promoDiscount = subtotal * (Number(appliedPromotion.value) / 100);
        }
    } else {
        promoDiscount = Number(appliedPromotion.value);
    }

    return { promoDiscount, promoDetails };
};

/**
 * Filter a tenant's discounts down to campaigns that a waiter can apply to a
 * walk-in / guest manual order (no customer profile, no coupon code entry).
 */
export const guestApplicablePromotions = (discounts) => {
    return (discounts || []).filter(d => {
        if (d.isActive === false) return false;
        if (d.isReward) return false;
        if (d.birthdayOnly) return false; // needs customer birthday
        if (d.campaignType === 'coupon' || d.campaignType === 'coupon_bundle') return false; // needs a code
        if (d.type === 'multiplier') return false; // guest earns no points
        if (d.target && d.target !== 'all') return false; // guest has no loyalty profile
        return true;
    });
};
